import { useState, useCallback } from 'react';
import { useLazyGetPreSignedUrlQuery } from '@/redux/attachment/attachment.api.slice';

// IMPORTANT: Must stay in sync with `backend/src/lib/s3.ts → CacheControl`.
// Presigned PUT uploads sign one of these values into the URL; if the
// client's `Cache-Control` request header doesn't match what the backend
// signed, S3 rejects with SignatureDoesNotMatch. Update both places together.
export const CacheControl = {
  IMMUTABLE: 'public, max-age=31536000, immutable',
  SHORT: 'public, max-age=300, must-revalidate',
  NO_CACHE: 'no-cache, no-store, must-revalidate',
} as const;

export async function getPublicFileFromS3(url: string): Promise<Response> {
  try {
    const response = await fetch(url, {
      cache: 'no-cache',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch file');
    }
    return response;
  } catch (error) {
    throw new Error('Failed to fetch file');
  }
}

export const useFileDownloader = () => {
  const [fetchPreSignedUrl] = useLazyGetPreSignedUrlQuery();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const downloadFile = useCallback(
    async (attachmentId: string, fileName: string) => {
      setLoading(true);
      setError(null);
      try {
        const preSignedUrl = await fetchPreSignedUrl({
          attachmentId,
        }).unwrap();
        if (!preSignedUrl) {
          throw new Error('Failed to fetch pre-signed URL');
        }
        const response = await fetch(preSignedUrl, {
          cache: 'no-cache',
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch file: ${response.status} ${response.statusText}`
          );
        }

        const blob = await response.blob();
        const link = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob);

        link.href = blobUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [fetchPreSignedUrl]
  );

  return { downloadFile, loading, error };
};
